// This device's key for the sealed store (hq:Decision/device-keys). The age identity is made, unwrapped and used only
// inside the platform's core (platform/core/features/secrets through wasm); the page keeps its wrapped form in local
// storage, and the secret that unwraps it is a passkey's PRF output (WebAuthn, user verification: Face ID), or a
// passphrase where the authenticator has no PRF. Every read asks the passkey again; nothing here keeps a key or a
// value, logs one or puts one in a URL.
import { platformSecrets } from "@hq-org/platform-wasm";
import { api } from "@/api";
import { WASM } from "@/api/net";

/** What this device keeps: the DeviceKey record, its public key, the wrapped identity, the passkey and PRF salt. */
type Stored = { id: string; public: string; wrapped: string; credential: string | null; salt: string; prf: boolean };
/** Asks for the passphrase (the fallback); null = cancelled. */
export type Ask = (repeat: boolean) => Promise<string | null>;

const slot = (person: string) => `hq.deviceKey.${person}`;
const b64 = (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b)));
const bytes = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const random = () => crypto.getRandomValues(new Uint8Array(32));
const prfOf = (c: Credential | null) => (c as PublicKeyCredential | null)?.getClientExtensionResults() as { prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } } } | undefined;

export const local = (person: string): Stored | null => JSON.parse(localStorage.getItem(slot(person)) ?? "null");

/** The passkey's user verification, and its PRF output when it has one. */
async function verify(k: Pick<Stored, "credential" | "salt">): Promise<string | null> {
	if (!k.credential) return null;
	const a = await navigator.credentials.get({
		publicKey: {
			challenge: random(),
			allowCredentials: [{ type: "public-key", id: bytes(k.credential) }],
			userVerification: "required",
			extensions: { prf: { eval: { first: bytes(k.salt) } } } as AuthenticationExtensionsClientInputs,
		},
	});
	const first = prfOf(a)?.prf?.results?.first;
	return first ? b64(first) : null;
}

/** The secret that unwraps the key: the passkey's PRF output, or (no PRF) the passkey's confirm and the passphrase. */
async function unlock(k: Stored, ask: Ask): Promise<string> {
	const prf = await verify(k);
	const secret = k.prf ? prf : await ask(false);
	if (!secret) throw new Error(k.prf ? "the passkey gave no key" : "cancelled");
	return secret;
}

/** Makes this device's key and registers it (a truss.DeviceKey of mine). A new passkey of its own (a random user
 *  handle, so it never replaces the sign-in passkey); no PRF (or no WebAuthn) → the passphrase wraps it. */
export async function setUp(person: { id: string; name: string }, name: string, ask: Ask): Promise<Stored> {
	const salt = random();
	let credential: string | null = null;
	let prf: string | null = null;
	if (window.PublicKeyCredential) {
		const c = await navigator.credentials.create({
			publicKey: {
				rp: { name: "hq secrets" },
				user: { id: random(), name: `${person.name} \u00b7 secrets \u00b7 ${name}`, displayName: `${person.name} (secrets, ${name})` },
				challenge: random(),
				pubKeyCredParams: [
					{ type: "public-key", alg: -7 },
					{ type: "public-key", alg: -257 },
				],
				authenticatorSelection: { userVerification: "required", residentKey: "discouraged" },
				extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
			},
		});
		credential = c ? b64((c as PublicKeyCredential).rawId) : null;
		const r = prfOf(c)?.prf;
		// some authenticators answer the PRF at creation, others only say it is there (iOS): ask it once
		prf = r?.results?.first ? b64(r.results.first) : r?.enabled ? await verify({ credential, salt: b64(salt) }) : null;
	}
	const secret = prf ?? (await ask(true));
	if (!secret) throw new Error("cancelled");
	const { deviceKey } = await platformSecrets(WASM);
	const k = JSON.parse(deviceKey(new Date().toISOString(), secret, !!prf)) as { public: string; wrapped: string };
	const id = await api.secrets.addDevice(name, k.public);
	const stored: Stored = { id, ...k, credential, salt: b64(salt), prf: !!prf };
	localStorage.setItem(slot(person.id), JSON.stringify(stored));
	return stored;
}

/** Forget this device's key here (after revoking it, or when it is gone from the instance). */
export const forget = (person: string) => localStorage.removeItem(slot(person));

/** One value, after the passkey: the GET names only its ref (one read event), the core opens it. */
export async function reveal(person: string, project: string, ref: string, ask: Ask): Promise<string> {
	const k = local(person);
	if (!k) throw new Error("no key on this device");
	const secret = await unlock(k, ask);
	const { open } = await platformSecrets(WASM);
	const v = (JSON.parse(open(k.wrapped, secret, await api.secrets.read(project, [ref]))) as Record<string, string>)[ref];
	if (v === undefined) throw new Error("gone");
	return v;
}

/** Re-seal the project's stale values to its current recipients from this device (a new device, a revoked one). */
export async function reseal(person: string, project: string, ask: Ask): Promise<number> {
	const k = local(person);
	if (!k) throw new Error("no key on this device");
	const secret = await unlock(k, ask);
	const core = await platformSecrets(WASM);
	return api.secrets.reseal(project, (body, current, endpoint) => core.reseal(k.wrapped, secret, endpoint, project, body, JSON.stringify(current)));
}
